import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAnalyticsConsent } from '~~/app/composables/useAnalyticsConsent'
import { useBrand } from '~~/app/composables/useBrand'
import {
  consentCookieName,
  CONSENT_COOKIE_MAX_AGE,
  CONSENT_VERSION,
  readDecision,
  type ConsentChoice,
  type StoredConsent,
} from '~~/app/utils/consent'
import { isSalesmanagoEnabled } from '~~/shared/utils/salesmanago'

export const useConsentStore = defineStore('consent', () => {
  // useCookie działa po obu stronach, ale baner i tak renderujemy w ClientOnly
  // (app.vue) — trasy są SWR-cache'owane, więc serwerowy odczyt cookie NIE chroni
  // przed migotaniem (cache współdzieli HTML między userami). Brak migotania =
  // ClientOnly + klientowy odczyt document.cookie.
  const cookie = useCookie<StoredConsent | null>(consentCookieName(useBrand()), {
    maxAge: CONSENT_COOKIE_MAX_AGE,
    sameSite: 'lax',
    secure: !import.meta.dev,
    path: '/',
  })

  // Pułapka kontekstu: useAnalyticsConsent korzysta z useGtag (kontekst Nuxt) —
  // resolvujemy composable RAZ w setupie store, nie w handlerze kliknięcia.
  const { setConsent } = useAnalyticsConsent()

  const forceOpen = ref(false)

  const decision = computed<ConsentChoice | null>(() => readDecision(cookie.value))
  const hasDecided = computed(() => decision.value !== null)
  const current = computed<ConsentChoice>(() => decision.value ?? { analytics: false, marketing: false })

  /**
   * Czy w tej instalacji jest w ogóle o co pytać?
   *
   * Zgoda dotyczy narzędzi analitycznych i marketingowych. Gdy żadne nie jest
   * skonfigurowane — jak w sklepie katalogowym, który ma tylko formularz
   * zapytania ofertowego — baner pytałby o zgodę na coś, czego nie robimy.
   * To nie jest kosmetyka: zgoda zbierana „na zapas" jest pozorna, a RODO każe
   * pytać o rzeczywisty zakres przetwarzania.
   *
   * Wszystkie te identyfikatory są runtime'owe (inventory ustawia je przy
   * starcie kontenera), więc ten sam obraz zachowa się inaczej na lavmagu z GTM
   * i na showcase'ie bez żadnego narzędzia.
   */
  const config = useRuntimeConfig()
  const hasTrackingToConsent = computed(() => {
    const publicConfig = config.public as Record<string, unknown>

    return Boolean(
      publicConfig.gtmId
      || publicConfig.gtagId
      || publicConfig.metaPixelId
      || publicConfig.edroneAppId
      || publicConfig.segmentifyAccountId
      // SALESmanago: ten sam warunek co loader sm.js (client ID + poprawny host +
      // brand z allowlisty) — sam client ID przy literówce w endpoincie pytałby o
      // zgodę na skrypt, który się nie załaduje.
      || isSalesmanagoEnabled(publicConfig),
    )
  })

  const isBannerVisible = computed(
    () => hasTrackingToConsent.value && (!hasDecided.value || forceOpen.value),
  )

  function persist(choice: ConsentChoice) {
    cookie.value = {
      v: CONSENT_VERSION,
      analytics: choice.analytics,
      marketing: choice.marketing,
      ts: Date.now(),
    }
  }

  function applyConsent(choice: ConsentChoice) {
    if (import.meta.client) setConsent(choice)
  }

  function commit(choice: ConsentChoice) {
    persist(choice)
    applyConsent(choice)
    forceOpen.value = false
  }

  function acceptAll() {
    commit({ analytics: true, marketing: true })
  }

  function rejectAll() {
    commit({ analytics: false, marketing: false })
  }

  function save(choice: ConsentChoice) {
    commit(choice)
  }

  function reopen() {
    forceOpen.value = true
  }

  function cancelReopen() {
    forceOpen.value = false
  }

  return {
    forceOpen,
    decision,
    hasDecided,
    current,
    hasTrackingToConsent,
    isBannerVisible,
    acceptAll,
    rejectAll,
    save,
    reopen,
    cancelReopen,
    applyConsent,
  }
})
